/**
 * Pure helpers behind the virtual-key create/edit form (ModelTokensPage).
 *
 * The edit form must never disturb a key's budget state: the PATCH body only
 * carries the fields whose value really changed, so renaming a key does not
 * re-send `budget_period` (which used to restart the spend window).
 */

export type BudgetPeriod = 'unlimited' | 'daily' | 'weekly' | 'monthly';
export type AssignType = 'none' | 'team' | 'user' | 'agent';

/** The editable fields of a stored key, as the API returns them. */
export interface VirtualKeyEditable {
  label: string;
  provider_key_id: string;
  rate_limit_rpm: number | null;
  rate_limit_tpd: number | null;
  assigned_team_id: string | null;
  assigned_email: string | null;
  assigned_agent_id: string | null;
  budget_amount: number | null;
  budget_period: BudgetPeriod;
  allowed_models: string[] | null;
}

/** What the antd form holds (empty inputs come back as undefined or null). */
export interface VirtualKeyFormValues {
  label: string;
  provider_key_id?: string;
  rate_limit_rpm?: number | null;
  rate_limit_tpd?: number | null;
  assigned_team_id?: string;
  assigned_email?: string;
  assigned_agent_id?: string;
  budget_amount?: number | null;
  budget_period?: BudgetPeriod;
  allowed_models?: string[];
}

/** Normalized body: every editable field, empty values as null. */
export type VirtualKeyBody = Omit<VirtualKeyEditable, 'provider_key_id'>;

export function assignTypeOf(key: VirtualKeyEditable): AssignType {
  if (key.assigned_team_id) return 'team';
  if (key.assigned_email) return 'user';
  if (key.assigned_agent_id) return 'agent';
  return 'none';
}

export function formValuesOf(key: VirtualKeyEditable): VirtualKeyFormValues {
  return {
    label: key.label,
    provider_key_id: key.provider_key_id,
    rate_limit_rpm: key.rate_limit_rpm ?? undefined,
    rate_limit_tpd: key.rate_limit_tpd ?? undefined,
    assigned_team_id: key.assigned_team_id ?? undefined,
    assigned_email: key.assigned_email ?? undefined,
    assigned_agent_id: key.assigned_agent_id ?? undefined,
    budget_period: key.budget_period,
    budget_amount: key.budget_amount ?? undefined,
    allowed_models: key.allowed_models ?? [],
  };
}

/** Builds the full normalized body from the form, shared by create and edit. */
export function buildVirtualKeyBody(values: VirtualKeyFormValues, assignType: AssignType): VirtualKeyBody {
  const period = values.budget_period ?? 'unlimited';
  return {
    label: values.label.trim(),
    rate_limit_rpm: values.rate_limit_rpm ?? null,
    rate_limit_tpd: values.rate_limit_tpd ?? null,
    // Only the selected assignment survives; the other two are cleared.
    assigned_team_id: assignType === 'team' ? values.assigned_team_id ?? null : null,
    assigned_email: assignType === 'user' ? values.assigned_email ?? null : null,
    assigned_agent_id: assignType === 'agent' ? values.assigned_agent_id ?? null : null,
    budget_amount: period !== 'unlimited' ? values.budget_amount ?? null : null,
    budget_period: period,
    allowed_models: values.allowed_models && values.allowed_models.length > 0 ? values.allowed_models : null,
  };
}

function sameValue(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && [...a].sort().every((v, i) => v === [...b].sort()[i]);
  }
  return (a ?? null) === (b ?? null);
}

/**
 * Only the fields of `body` that differ from the stored key. An empty result
 * means nothing changed and no request is needed.
 */
export function changedVirtualKeyFields(original: VirtualKeyEditable, body: VirtualKeyBody): Partial<VirtualKeyBody> {
  const changed: Partial<VirtualKeyBody> = {};
  (Object.keys(body) as Array<keyof VirtualKeyBody>).forEach((field) => {
    if (!sameValue(original[field], body[field])) {
      (changed as Record<string, unknown>)[field] = body[field];
    }
  });
  return changed;
}
